import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = '/api/users';

// GET /api/users needs `users.read`, which Receptionist does not hold — it is
// an admin permission and granting it would expose the whole staff directory
// just to fill a picker. But GET /api/users/mechanics/available sits behind
// `protect` with no permission gate, returns the same {success, data:[user]}
// shape, and is already the right list for every assignee picker.
//
// So a caller that only wants assignable staff passes `mechanicsOnly` and gets
// the open endpoint, with a 403 on the admin one falling back to it as well —
// a role that CAN read users keeps the full list, one that cannot still gets a
// working picker instead of an empty modal.
//
// `assignable_in_branch` is the users-endpoint spelling; the mechanics endpoint
// calls the same scope `branch`. Translating it is not cosmetic: dropping it
// would silently widen the picker to the caller's own branch and offer staff the
// assignee validator then refuses.
const toMechanicsParams = (params = {}) => {
  const { assignable_in_branch: branch, branch: explicitBranch } = params;
  const scope = explicitBranch || branch;
  return scope ? { branch: scope } : {};
};

const getMechanics = async (params) => {
  const response = await axios.get(`${API_URL}/mechanics/available`, {
    params: toMechanicsParams(params),
  });
  return response.data;
};

export const fetchUsers = createAsyncThunk('users/fetchAll', async (params, { rejectWithValue }) => {
  try {
    if (params?.mechanicsOnly) return await getMechanics(params);
    const response = await axios.get(API_URL, { params });
    return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    // A picker asking for assignable staff must not die on the admin gate.
    if (err.response?.status === 403 && !params?.mechanicsOnly) {
      try {
        return await getMechanics(params);
      } catch {
        // fall through to the original 403 — the real reason beats a second one
      }
    }
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

export const createUser = createAsyncThunk('users/create', async (data, { rejectWithValue }) => {
  try {
    const response = await axios.post(API_URL, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { error: error.message });
  }
});

export const getAvailableMechanics = createAsyncThunk('users/mechanics', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get(`${API_URL}/mechanics/available`);
    return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

export const fetchRoles = createAsyncThunk('users/roles', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get(`${API_URL}/roles`);
    return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

export const updateUser = createAsyncThunk('users/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await axios.put(`${API_URL}/${id}`, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { error: error.message });
  }
});

export const toggleUserStatus = createAsyncThunk('users/toggleStatus', async (id, { rejectWithValue }) => {
  try {
    const response = await axios.patch(`${API_URL}/${id}/toggle-status`);
    return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

export const deleteUser = createAsyncThunk('users/delete', async (id, { rejectWithValue }) => {
  try {
    const response = await axios.delete(`${API_URL}/${id}`);
    return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

const userSlice = createSlice({
  name: 'users',
  initialState: {
    items: [],
    mechanics: [], // mechanics specific list
    roles: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.items = action.payload.data;
      })
      .addCase(getAvailableMechanics.fulfilled, (state, action) => {
        state.mechanics = action.payload.data;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.roles = action.payload.data;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        // Ensure items is an array before unshift
        if (!Array.isArray(state.items)) {
          state.items = [];
        }
        state.items.unshift(action.payload.data);
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item.id === action.payload.data.id);
        if (index !== -1) {
          // MERGE, never replace: the row keeps anything the response did not
          // carry. PUT /api/users/:id answers with the full list shape, but a
          // bare-instance answer must not strip role/garage off the row — that
          // is what made a freshly-edited user vanish from the list.
          state.items[index] = { ...state.items[index], ...action.payload.data };
        }
      })
      .addCase(toggleUserStatus.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item.id === action.payload.data.id);
        if (index !== -1) {
          state.items[index] = { ...state.items[index], ...action.payload.data };
        }
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        // Soft delete: update user status to inactive
        const index = state.items.findIndex(item => item.id === action.payload.data.id);
        if (index !== -1) {
          state.items[index] = { ...state.items[index], ...action.payload.data };
        }
      });
  },
});

export default userSlice.reducer;
